/**
 * Profile Source Modal
 *
 * Shows users where proposal section content comes from in their profile,
 * what's missing, and how to improve it.
 *
 * Uses BidWin brand colors for consistency.
 */

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ExternalLink,
  Lightbulb,
  FileText,
  ArrowRight,
  Info,
  BarChart3,
  Database,
  BookOpen,
} from 'lucide-react';
import {
  SECTION_PROFILE_MAPPINGS,
  analyzeProfileGaps,
  normalizeSectionType,
  type ProfileFieldMapping,
} from '../../lib/section-profile-mapping';

interface ProfileSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  sectionType: string;
  sectionTitle: string;
  profile: any; // The user's profile data
}

export function ProfileSourceModal({
  isOpen,
  onClose,
  sectionType,
  sectionTitle,
  profile,
}: ProfileSourceModalProps) {
  const navigate = useNavigate();

  // Get the mapping for this section
  const normalizedType = normalizeSectionType(sectionType);
  const mapping = SECTION_PROFILE_MAPPINGS[normalizedType];

  // Analyze profile gaps
  const gapAnalysis = useMemo(() => {
    if (!mapping || !profile) return null;
    return analyzeProfileGaps(normalizedType, profile);
  }, [normalizedType, profile, mapping]);

  if (!isOpen) return null;

  // Handle navigation to profile section
  const handleEditProfile = (path: string) => {
    onClose();
    navigate(path);
  };

  // If no mapping exists for this section
  if (!mapping) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
        <div className="bg-bw-surface dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-bw-ink dark:text-white">
              Profile Source
            </h2>
            <button onClick={onClose} className="p-2 text-bw-muted hover:text-bw-muted rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-bw-muted dark:text-bw-muted">
            Profile source mapping is not yet available for this section.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-bw-surface dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header - BidWin Blue */}
        <div className="px-6 py-4 border-b border-bw-border dark:border-gray-700 bg-bw-sidebar">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Database className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">
                  Profile Data Source
                </h2>
                <p className="text-sm text-white/80">
                  {sectionTitle}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Description */}
          <div className="p-4 bg-bw-primary-tint dark:bg-bw-primary/10 rounded-lg border border-servet-blue/20">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-bw-primary flex-shrink-0 mt-0.5" />
              <p className="text-sm text-bw-ink dark:text-gray-300">
                {mapping.description}
              </p>
            </div>
          </div>

          {/* Profile Completeness Score */}
          {gapAnalysis && (
            <div className="p-4 bg-bw-surface-subtle dark:bg-gray-800/50 rounded-lg border border-bw-border dark:border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <BarChart3 className="w-5 h-5 text-bw-muted dark:text-bw-muted" />
                  <span className="font-medium text-bw-ink dark:text-white">
                    Profile Data Completeness
                  </span>
                </div>
                <span className={`text-2xl font-bold ${
                  gapAnalysis.score >= 80
                    ? 'text-success'
                    : gapAnalysis.score >= 50
                    ? 'text-fit-moderate'
                    : 'text-urgent'
                }`}>
                  {gapAnalysis.score}%
                </span>
              </div>
              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    gapAnalysis.score >= 80
                      ? 'bg-success-bg0'
                      : gapAnalysis.score >= 50
                      ? 'bg-fit-moderate-bg0'
                      : 'bg-urgent-bg0'
                  }`}
                  style={{ width: `${gapAnalysis.score}%` }}
                />
              </div>
              <p className="text-xs text-bw-muted dark:text-bw-muted mt-2">
                {gapAnalysis.present.length} of {gapAnalysis.totalFields} data sources available
              </p>
            </div>
          )}

          {/* Data Sources */}
          <div>
            <h3 className="text-sm font-semibold text-bw-ink dark:text-white mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4 text-bw-primary" />
              Data Sources
            </h3>
            <div className="space-y-2">
              {mapping.profileSources.map((source, idx) => {
                const hasData = source.checkFn ? source.checkFn(profile) : false;
                return (
                  <div
                    key={idx}
                    className={`p-3 rounded-lg border transition-colors ${
                      hasData
                        ? 'border-green-200 dark:border-green-800 bg-success-bg/50 dark:bg-green-900/10'
                        : source.required
                        ? 'border-red-200 dark:border-red-800 bg-urgent-bg/50 dark:bg-red-900/10'
                        : 'border-amber-200 dark:border-amber-800 bg-fit-moderate-bg/50 dark:bg-amber-900/10'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        {hasData ? (
                          <CheckCircle2 className="w-5 h-5 text-success dark:text-green-400 flex-shrink-0 mt-0.5" />
                        ) : source.required ? (
                          <XCircle className="w-5 h-5 text-urgent dark:text-red-400 flex-shrink-0 mt-0.5" />
                        ) : (
                          <AlertTriangle className="w-5 h-5 text-fit-moderate dark:text-amber-400 flex-shrink-0 mt-0.5" />
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-bw-ink dark:text-white text-sm">
                              {source.label}
                            </span>
                            {source.required && !hasData && (
                              <span className="text-xs px-1.5 py-0.5 bg-urgent-bg dark:bg-red-900/30 text-urgent dark:text-red-300 rounded font-medium">
                                Required
                              </span>
                            )}
                            {!source.required && !hasData && (
                              <span className="text-xs px-1.5 py-0.5 bg-fit-moderate-bg dark:bg-amber-900/30 text-fit-moderate dark:text-amber-300 rounded">
                                Recommended
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-bw-muted dark:text-bw-muted mt-0.5">
                            {source.description}
                          </p>
                        </div>
                      </div>
                      {!hasData && (
                        <button
                          onClick={() => handleEditProfile(source.profilePath)}
                          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-bw-primary hover:text-servet-dark-blue bg-bw-primary/10 hover:bg-bw-primary/20 rounded transition-colors"
                        >
                          Add
                          <ArrowRight className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Missing Required Fields Alert */}
          {gapAnalysis && gapAnalysis.missingRequired.length > 0 && (
            <div className="p-4 bg-urgent-bg dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-urgent dark:text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-urgent dark:text-red-200 text-sm">
                    Action Required: Missing Data
                  </h4>
                  <p className="text-xs text-urgent dark:text-red-300 mt-1">
                    The following required information is missing from your profile.
                    Adding this data will significantly strengthen this section.
                  </p>
                  <ul className="mt-2 space-y-1">
                    {gapAnalysis.missingRequired.map((field, idx) => (
                      <li key={idx} className="text-xs text-urgent dark:text-red-300 flex items-center gap-2">
                        <span className="w-1 h-1 bg-urgent-bg0 rounded-full" />
                        <span className="flex-1">{field.label}</span>
                        <button
                          onClick={() => handleEditProfile(field.profilePath)}
                          className="text-urgent dark:text-red-400 hover:underline font-medium"
                        >
                          Add →
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Tips Section */}
          <div>
            <h3 className="text-sm font-semibold text-bw-ink dark:text-white mb-3 flex items-center gap-2">
              <Lightbulb className="w-4 h-4 text-fit-moderate" />
              Tips for {mapping.sectionTitle}
            </h3>
            <ul className="space-y-2 bg-fit-moderate-bg/50 dark:bg-amber-900/10 rounded-lg p-4 border border-amber-200/50 dark:border-amber-800/50">
              {mapping.tips.map((tip, idx) => (
                <li
                  key={idx}
                  className="flex items-start gap-2 text-sm text-bw-ink dark:text-gray-300"
                >
                  <span className="w-1.5 h-1.5 bg-fit-moderate-bg0 rounded-full mt-2 flex-shrink-0" />
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Best Practices */}
          <div>
            <h3 className="text-sm font-semibold text-bw-ink dark:text-white mb-3 flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-bw-primary" />
              Federal Proposal Best Practices
            </h3>
            <div className="bg-bw-surface-subtle dark:bg-gray-800/50 rounded-lg p-4 border border-bw-border dark:border-gray-700">
              <ul className="space-y-2">
                {mapping.bestPractices.map((practice, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-2 text-sm text-bw-muted dark:text-bw-muted"
                  >
                    <CheckCircle2 className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                    <span>{practice}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-bw-surface-subtle dark:bg-gray-800/50 border-t border-bw-border dark:border-gray-700 flex justify-between items-center">
          <button
            onClick={() => handleEditProfile('/profile')}
            className="flex items-center gap-2 px-4 py-2 text-bw-primary hover:text-servet-dark-blue font-medium transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Edit Full Profile
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-bw-primary text-white rounded-lg hover:bg-servet-dark-blue transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProfileSourceModal;
